import React, {DragEvent, FC, PropsWithChildren} from 'react';
import {useDrag} from 'react-dnd';

import {addDownloadToDragEvent} from '../../../../../helpers/dragAndDropHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  UploadedAttachmentModel,
} from '../../../../../models/api/attachmentModel';
import {
  addNativeAttachmentsToDragEvent,
  DraggedAttachmentItemTypesEnum,
} from '../../../../common/attachments/attachmentsDragAndDrop';

/*
 * Constants.
 */

export const AttachmentDragType = 'ATTACHMENT';

/*
 * Props.
 */

interface AttachmentDragSourceProps {
  className?: string;
  attachment: AttachmentModel;
}

export type AttachmentDragItem = UploadedAttachmentModel;

/*
 * Component.
 */

const dragSourceOptions = Object.freeze({dropEffect: 'copy'});

export const AttachmentDragSource: FC<PropsWithChildren<AttachmentDragSourceProps>> = (props) => {
  const [, drag] = useDrag({
    type: AttachmentDragType,
    item: props.attachment,
    options: dragSourceOptions,
  });

  const onDragStart = (e: DragEvent, attachment: UploadedAttachmentModel) => {
    addNativeAttachmentsToDragEvent(e, {
      type: DraggedAttachmentItemTypesEnum.UPLOADED_ATTACHMENTS,
      attachments: [attachment],
    });

    // Setting effectAllowed = 'copy' is needed in addition to the
    // dropEffect: 'copy' drag source option, since that only has the
    // desired effect when dragging over something in the page, not when
    // communicating with the host OS.
    e.dataTransfer.effectAllowed = 'copy';
    addDownloadToDragEvent(e, {
      name: attachment.name || 'attachment.dat',
      url: attachment.url.startsWith('data:') ? attachment.url : `${location.origin}${attachment.url}`,
    });
  };

  const {attachment, children} = props;
  if (attachment.status !== AttachmentStatusesEnum.UPLOADED) {
    return <>{children}</>;
  }

  return (
    <div ref={drag} className={props.className} onDragStart={(e) => onDragStart(e, attachment)}>
      {children}
    </div>
  );
};
